import { Link as RouterLink } from 'react-router-dom'
import { useModal } from '../../../_components/Modal'
import { shallowEqual, useSelector, useStore } from 'react-redux'
import { masterClassification, titleName } from '../../../_constants/Code'
import { Link } from '@mui/material'
import CustomTooltip from '../../../_components/CustomTooltip'
import { TypeFilter } from '../../common/TypeFilter/TypeFilter'
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import { produce } from 'immer'
import { fetchChrofyClassificationRecords, fetchChrofyClassificationType, fetchMaster } from './extra/services'
import { useModifyClassList } from '../../../common/utils/CSSUtils'
import _set from 'lodash/set'
import { ChrofyClassificationPageState } from './extra/types'
import Forbidden from '../../../_components/Forbidden'
import PageLoading from '../../../_components/PageLoading'

// 定数定義
const MASTER_DATA_MAPPING = 'dataMapping'
const ERROR_MESSAGES = {
  DATA_FETCH_ERROR: 'データ取得エラーが発生しました',
  NO_DATA: '項目が登録されていません',
  NO_SELECTION: '区分を選択してください',
  NO_DESCRIPTION: '説明が登録されていません。'
}
// 値が未設定の場合に表示するプレースホルダー
const EMPTY_CELL_LABEL = '（空）'

/**
 * テーブルセルの値が空の場合にグレーのプレースホルダーを返す
 *
 * @param {string} text - 表示する値
 * @returns {string | JSX.Element} 値、または未設定プレースホルダー
 */
const renderEmptyableCell = (text) => (text?.trim() ? text : <span className="text-gray-400">{EMPTY_CELL_LABEL}</span>)

/**
 * @function chrofyClassificationReducer
 * @param {import('./extra/types')._ChrofyClassificationPageState} state
 * @param {{ payload: any; type: string }} action
 */
const chrofyClassificationReducer = (state, action) => {
  const { payload, type } = action
  return produce(state, (draft) => {
    switch (type) {
      case 'INIT': {
        const { types, records } = payload

        // 区分IDをキーとしたMap形式で保持
        const tempRecords = Object.fromEntries(types.map((e) => [e.code, []]))

        // 区分項目を分類キーでグループ化
        for (const record of records) {
          const classificationKey = record.classificationKey
          if (tempRecords[classificationKey]) {
            tempRecords[classificationKey].push(record)
          }
        }

        // 各区分の項目数を計算してitemCountに設定
        const typesWithItemCount = types.map((type) => {
          const itemCount = tempRecords[type.code]?.length ?? 0
          return {
            ...type,
            itemCount
          }
        })

        draft.records = tempRecords
        draft.types = typesWithItemCount

        // 最初の区分（DisplayOrderが最小）を自動選択
        if (typesWithItemCount.length > 0) {
          draft.selectedType = typesWithItemCount[0].code
        }
        break
      }
      case 'SET_STATE': {
        _set(draft, payload.path, payload.value)
        break
      }
      case 'SET_ERROR': {
        draft.serverErrors = payload.errors
        break
      }
      default:
        break
    }
  })
}

const ChrofyClassificationInfoContent = ({ showModal }) => {
  const [master, setMaster] = useState({})
  // 初期表示のデータ読み込み中のローディング表示制御
  const [loading, setLoading] = useState(true)
  const [state, _dispatch] = useReducer(
    chrofyClassificationReducer,
    {
      records: {},
      types: [],
      selectedType: '',
      serverErrors: []
    },
    ChrofyClassificationPageState.parse
  )

  const { records, types, selectedType, serverErrors } = state

  const dispatch = useCallback((type, payload) => _dispatch({ type, payload }), [])
  const store = useStore()

  useEffect(() => {
    document.title = titleName.chrofyClassificationInfo + titleName.common
  }, [])

  const init = useCallback(async () => {
    try {
      // 区分一覧を取得
      const pTypes = fetchChrofyClassificationType(store)
      const pMaster = fetchMaster(store.getState().company.corporationNumber)

      const [types, masterData] = await Promise.all([pTypes, pMaster])
      setMaster(masterData)

      if (types.length === 0) {
        dispatch('SET_ERROR', { errors: [ERROR_MESSAGES.DATA_FETCH_ERROR] })
        return
      }

      // 全区分の項目を並列取得
      const records = await fetchChrofyClassificationRecords(store, types)

      dispatch('INIT', {
        types,
        records
      })
    } catch (error) {
      console.error('初期表示エラー:', error)
      dispatch('SET_ERROR', { errors: [ERROR_MESSAGES.DATA_FETCH_ERROR] })
    } finally {
      setLoading(false)
    }
  }, [store, dispatch])

  useEffect(() => {
    init()
  }, [init])

  const selectedChrofyClassificationType = types.find((e) => e.code === selectedType)

  const sortedRecords = useMemo(() => {
    return selectedType ? (records[selectedType] ?? []) : []
  }, [records, selectedType])

  // モーダルの幅を広げるためのフック
  const { promisify: promisifyModalWidth } = useModifyClassList('.modal-window', ['w-[600px]'])

  /** @function openMemoModal 区分の説明モーダルを表示 */
  const openMemoModal = useCallback(
    async (type) => {
      if (!showModal) return

      try {
        await promisifyModalWidth((res) => {
          showModal({
            title: type.label || '区分の説明',
            isWide: false,
            isFooter: false,
            onClose: res,
            titleAlign: 'left',
            closeButtonStyle: 'icon',
            body: (
              <div className="py-4">
                <p className="text-base text-gray-700 whitespace-pre-wrap">
                  {type.memo || ERROR_MESSAGES.NO_DESCRIPTION}
                </p>
              </div>
            )
          })
        })
      } catch (error) {
        // モーダル表示エラーはユーザーに影響しないため、ログのみ記録
        console.error('区分説明モーダル表示エラー:', error)
      }
    },
    [showModal, promisifyModalWidth]
  )

  /** @function onSelectItem 区分を選択 */
  const onSelectItem = useCallback(
    (e) => {
      dispatch('SET_STATE', {
        path: 'selectedType',
        value: e.code
      })
    },
    [dispatch]
  )

  /** @function recordCountGetter 区分の登録数を取得 */
  const recordCountGetter = useCallback(
    (code) => {
      return records[code]?.length ?? 0
    },
    [records]
  )

  if (loading) {
    return <PageLoading />
  }

  return (
    <main className="page-container">
      <section className="page-content">
        <div className="centered-container">
          {/* 上階層に戻る */}
          <div className="bg main">
            <div className="back-upper-level py-8 pl-8 border-b border-blue2-lighten-20">
              <Link color="inherit" variant="button" underline="none" component={RouterLink} to="/master/masterList">
                <span className="back-upper-level__link link--text hover:lighten-70 flex items-center">
                  <span className="icon-arrow-circle icon-32px mr-4 rotate-180 relative after:absolute after:bg-white after:w-full after:h-full after:rounded-full after:left-0 after:-z-10"></span>
                  <span className="text-h5 font-bold">{titleName.masterList}画面に戻る</span>
                </span>
              </Link>
            </div>
          </div>

          {/* コンテンツヘッダ */}
          <header className="page-header flex justify-center py-1">
            <h1 className="page-header__name text-h1 grey--text py-8">{'区分関連マスター'}</h1>
          </header>
          <div className="page-body">
            <div id="serverErrors">
              {serverErrors?.map((e, index) => (
                <p key={index} className="red--text">
                  {e}
                </p>
              ))}
            </div>

            <div className="box-xy-bordered rounded-t-3xl rounded-b-2xl pt-10 pb-8 px-16 border-blue1">
              <h2 className="box-bordered-heading blue1 white--text text-h3 font-medium pt-2.5 pb-3 px-14 -mt-10 -mx-16 rounded-t-2xl">
                {'区分マスター画面（CHROFY規定値）'}
              </h2>

              <div className="my-6">
                システム規定の区分マスターデータを参照します。この画面では値の編集はできません。
              </div>

              <section className="flex gap-3 h-[1000px] max-h-[1000px] min-h-0 overflow-hidden">
                <div className="w-[30%] max-w-[300px] h-full min-h-0 border border-gray-300 overflow-y-auto">
                  <TypeFilter
                    title="区分"
                    types={types}
                    onSelectItem={onSelectItem}
                    selectedItemCode={selectedType || ''}
                    recordCountGetter={recordCountGetter}
                    onShowMemoModal={openMemoModal}
                  />
                </div>

                <div className="border border-gray-300 flex-1 overflow-y-auto">
                  <div className="p-3 px-5 h-[70px]">
                    <h2 className="font-medium">{selectedChrofyClassificationType?.label ?? ''}</h2>
                    {selectedChrofyClassificationType && (
                      <span className="text-sm text-gray-600">
                        登録数：{recordCountGetter(selectedChrofyClassificationType.code)}件
                      </span>
                    )}
                  </div>

                  <div className="border-t border-gray-300 p-4 pt-0 relative min-h-[70%]">
                    <div className="setting-table is-segment mt-5">
                      {!selectedType ? (
                        <p className="red--text text-center">{ERROR_MESSAGES.NO_SELECTION}</p>
                      ) : sortedRecords.length === 0 ? (
                        <p className="red--text text-center">{ERROR_MESSAGES.NO_DATA}</p>
                      ) : (
                        <table
                          className="setting-table__table table-fixed w-full form-control mb-5"
                          data-testid="chrofyClassificationInfoTable"
                        >
                          <colgroup>
                            <col className="w-[200px]" />
                            <col className="min-w-[200px]" />
                          </colgroup>
                          <thead className="sticky top-0 z-[39]">
                            <tr>
                              <th>コード</th>
                              <th>値</th>
                            </tr>
                          </thead>

                          <tbody>
                            {sortedRecords.map((e) => {
                              const rowKey = `chrofy-classification-row-${e.key}`
                              const isValueEmpty = !e.value?.trim()
                              return (
                                <tr key={rowKey} data-testid={rowKey}>
                                  <td data-testid="chrofyClassificationTableCol_code">{renderEmptyableCell(e.code)}</td>
                                  <td data-testid="chrofyClassificationTableCol_value">
                                    {isValueEmpty ? (
                                      <div className="truncate">{renderEmptyableCell(e.value)}</div>
                                    ) : (
                                      <CustomTooltip title={e.value}>
                                        {(ref) => (
                                          <div ref={ref} className="truncate">
                                            {e.value}
                                          </div>
                                        )}
                                      </CustomTooltip>
                                    )}
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </div>
              </section>

              <footer className="content-footer my-8">
                <div className="footer_button_area mt-8 justify-center">
                  <Link
                    color="inherit"
                    variant="button"
                    underline="none"
                    component={RouterLink}
                    to="/master/masterList"
                  >
                    <button type="button" className="footer-btn button_back">
                      戻る
                    </button>
                  </Link>
                </div>
              </footer>
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}

const ChrofyClassificationInfo = () => {
  const showModal = useModal()

  const state = useSelector((state) => {
    return {
      authority: state.authority
    }
  }, shallowEqual)

  // 権限を持っていない場合は画面表示しない
  if (
    !state.authority.Master_AuthInfo.Availability ||
    !state.authority.Master_AuthInfo.Auth_Conf.DataMapping.ChrofyClassificationItem_Set
  ) {
    return <Forbidden />
  }

  return <ChrofyClassificationInfoContent showModal={showModal} />
}

export default ChrofyClassificationInfo
